import { useEffect, useMemo } from 'react';
import {
  Filters,
  PermissionEnum,
  SchemaFilterType,
  useFilters,
  useGetList,
  useQueryState,
} from '@ui/src';
import { useLazyGetCustomersMonitoringRulesQuery } from '../../../customers/store/services/customers';
import {
  transactionMonitoringRulesFiltersSchema,
  transactionMonitoringRulesSchema,
} from '../variables/monitoring-rules';
import formatTransationMonitoringRuleList from '../helpers/format-monitoring-rules';
import { TransactionMonitoringRule } from '../../types';

const useMonitoringRules = ({
  monitoringRules,
  activePermissions,
  customerId,
}: {
  monitoringRules?: TransactionMonitoringRule[];
  activePermissions?: PermissionEnum[];
  customerId?: string;
}) => {
  const [getRules, { isLoading, isFetching }] =
    useLazyGetCustomersMonitoringRulesQuery();

  const { sort, onSortChange, onFilterChange, filters } = useQueryState(
    transactionMonitoringRulesSchema,
    true,
  );

  const { setPage, onFilter, errorList } = useGetList({
    getList: getRules,
    onFilterChange,
    onSortChange,
    schema: transactionMonitoringRulesSchema,
    withoutChangeSearchParams: true,
  });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: transactionMonitoringRulesFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const formattedData = useMemo(() => {
    const data = formatTransationMonitoringRuleList(
      monitoringRules as TransactionMonitoringRule[],
    );

    return data;
  }, [monitoringRules, sort]);

  const onRowClick = (id: string) => {
    window.open(`/transaction-monitoring-rules/${id}`);
  };

  const onTableFilterChange = (values: Filters) => {
    const nextValues = { ...values };

    nextValues.organizationIds = {
      value: [customerId as string],
      type: SchemaFilterType.STRING,
    };

    onFilter(nextValues);
  };

  const onTableSetPage = (data: { id: string; take: string }) => {
    const nextFilters: Filters = { ...filters };

    nextFilters.organizationIds = {
      value: [customerId as string],
      type: SchemaFilterType.STRING,
    };

    setPage({ ...data, filters: nextFilters });
  };

  useEffect(() => {
    if (
      !monitoringRules &&
      sort &&
      Object.keys(sort).length &&
      activePermissions?.includes(PermissionEnum.AML_MONITORING_RULES_LIST) &&
      customerId
    ) {
      onFilter({
        ...filters,
        organizationIds: {
          value: [customerId],
          type: SchemaFilterType.STRING,
        },
      });
    }
  }, [monitoringRules, sort, activePermissions, customerId]);

  return {
    formattedData,
    filters,
    onTableFilterChange,
    isLoading: isLoading || isFetching,
    filtersComponent,
    handleSubmit,
    errorList,
    onTableSetPage,
    onRowClick,
  };
};

export default useMonitoringRules;
